import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Sticky, { RenderPropSticky } from 'react-sticky-el';

import { CartContextProvider } from '@/contexts/CartContext';
import { ensureReCaptchaReady } from '@/external/service/paypal.service';
import { TmCart } from '@/tscapi/model/cart';
import { getAccountCart, paypalReviewGuestOrder } from '@/tscapi/service/accountcart.service';
import { getCart, approvePayPalAndUpdateCartPayment } from '@/tscapi/service/cart.service';
import { Cookie } from '@/tscapi/utils/cookie';

import { hasGuestEmail } from '../signin/utils/sessionStorage';

import { AccountCredit } from './accountcredit.component';
import { ContinueToPayment } from './continue-payment.component';
import { ContinueToReview } from './continue-review.component';
import { CreateAccount } from './create-account.component';
import { CreatePayment } from './create-payment.component';
import { FreeShippingNotice } from './FreeShippingNotice';
import { Giftcard } from './giftcard.component';
import { Header } from './header.component';
import { ItemSummary } from './item-summary.component';
import { LanguageToggle } from './language-toggle.component';
import { Loading } from './loading.component';
import { NotificationMsg } from './notification-msg.component';
import { OrderSummary } from './order-summary.component';
import { PaymentMethod } from './payment-method.component';
import { PaymentOption } from './payment-option.component';
import { Placeorder } from './place-order.component';
import { Promocode } from './promocode.component';
import { ShipByNote } from './ship-by-note.component';
import { ShippingAddress } from './shipping-address.component';
import { ShippingMethod } from './shipping-method.component';
import { ShippingFeeNoticeModal } from './ShippingFeeNoticeModal';

const ExpressCheckout = () => {
    const { i18n, t } = useTranslation();
    // temp state values:  address,payment,or 'review order'
    const [checkoutStep, setCheckoutStep] = useState('');
    const [cartData, setCartData] = useState<TmCart | null>(null);
    const [isQuebec, setIsQuebec] = useState(false);

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };
    let isAuthenticated = false;
    const cookie: Cookie = new Cookie();
    const analyticsData = window['analyticsData'] || {};

    const pageCssClass = () => {
        if (checkoutStep == 'payment' || checkoutStep == 'create-account') {
            return 'expressCheckout mobile-layout__sticky--short';
        } else {
            return 'expressCheckout mobile-layout__sticky--long';
        }
    };
    const queryClient = useQueryClient();

    const scrollToTop = () => {
        // Scroll to the error message container if it exists
        setTimeout(() => {
            const errorContainer = document.querySelector('.notice-card, .alert-danger');
            if (errorContainer) {
                // Adjust scroll position to account for sticky header
                const isMobile = window.innerWidth < 768;
                const headerOffset = isMobile ? 80 : 5; //80 is mobile sticky header, 5 gives slignt buffer on desktop
                const elementPosition = errorContainer.getBoundingClientRect().top + window.scrollY;
                const offsetPosition = elementPosition - headerOffset;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                });
            } else {
                // Fallback to top of page
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }, 100);
    };
    useEffect(() => {
        const checkoutPixelEvent = new CustomEvent('checkoutEvent', {
            detail: cartData,
        });
        document.dispatchEvent(checkoutPixelEvent);

        const reCaptchaSiteKey = window.tscConfig['GoogleReCaptchaV3SiteKey'] as string;
        ensureReCaptchaReady(reCaptchaSiteKey).catch((error) =>
            console.error('Error initializing reCAPTCHA globally:', error),
        );
        let checkoutState = '';
        const lang = cookie.Language.toLowerCase() == 'en_ca' ? 'en' : 'fr';
        changeLanguage(lang);

        const redirectFromCartUrl = window.location.href;
        const currentUrl = new URL(redirectFromCartUrl.toLowerCase());
        const source = currentUrl.searchParams.get('source');
        if (
            cookie.UserAccessToken != undefined &&
            cookie.UserAccessToken.Token.length > 0 &&
            cookie.UserAccessToken.Edp > 0
        ) {
            isAuthenticated = true;
            if (source && source == 'shoppingbag') {
                checkoutState = 'paypal-review-order';
            } else if (cookie.ReferrerUrl == '/pages/checkoutcreateaccount') {
                setCheckoutStep('payment');
                cookie.deleteReferrerUrl();
            }
        } else {
            isAuthenticated = false;

            if (source && source == 'shoppingbag') {
                // guest paypal checkout
                checkoutState = 'paypal-guestaccount';
            } else if (!cookie.ExpressCheckoutCreateAccount && !checkoutStep) {
                cookie.TargetUrl = '/pages/expresscheckout';
                window.location.href = '/pages/signin';
            } else {
                cookie.deleteExpressCheckoutCreateAccount();
                checkoutState = 'create-account';
                setCheckoutStep('create-account');
            }
        }

        const existingCustomerGuest =
            typeof window !== 'undefined' && sessionStorage.getItem('TSC_ExistingCustomerGuest') === 'true';
        let customerType: string;
        if (isAuthenticated && cookie.TargetUrl === '/pages/expresscheckout') {
            customerType = 'ACCOUNT';
        } else if (cookie.TargetUrl === '/pages/expresscheckout' && hasGuestEmail()) {
            customerType = existingCustomerGuest ? 'ACCOUNT_GUEST' : 'GUEST';
        }
        analyticsData.user = analyticsData.user || {};
        analyticsData.user.userType = customerType;
        window['analyticsData'] = analyticsData;

        const fetchData = async () => {
            try {
                let cartResponse: TmCart;
                if (checkoutState == 'paypal-guestaccount') {
                    try {
                        // Ensure reCAPTCHA is loaded and ready before executing
                        await ensureReCaptchaReady(reCaptchaSiteKey);
                        const token = await window.grecaptcha.execute(reCaptchaSiteKey, {
                            action: 'createaccountcheckout',
                        });
                        cartResponse = await paypalReviewGuestOrder(token);
                        if (!cartResponse) {
                            throw new Error('Failed to retrieve cart response during PayPal guest checkout.');
                        }
                    } catch (error) {
                        console.error('Error during PayPal guest checkout:', error);
                        throw new Error('Failed to retrieve cart response during PayPal guest checkout.');
                    }

                    //save cart to RQ cache
                    queryClient.setQueryData(
                        ['bag', cartResponse?.CartGuid, cookie.UserAccessToken?.Edp, cookie.Language],
                        cartResponse as TmCart,
                    );
                    setCartData(cartResponse);
                } else if (checkoutState == 'paypal-review-order') {
                    const result = await approvePayPalAndUpdateCartPayment();
                    cartResponse = result.data as TmCart;
                    //save cart to RQ cache
                    queryClient.setQueryData(
                        ['bag', result?.data?.CartGuid, cookie.UserAccessToken?.Edp, cookie.Language],
                        result.data as TmCart,
                    );
                    setCartData(cartResponse);
                } else if (checkoutState == 'create-account') {
                    const result = await getCart();
                    cartResponse = result.data as TmCart;
                    //save cart to RQ cache
                    queryClient.setQueryData(
                        ['bag', result?.data?.CartGuid, cookie.UserAccessToken?.Edp, cookie.Language],
                        result.data as TmCart,
                    );
                    setCartData(cartResponse);
                } else if (isAuthenticated) {
                    const result = await getAccountCart();
                    cartResponse = result.data as TmCart;
                    //save cart to RQ cache
                    queryClient.setQueryData(
                        ['bag', result?.data?.CartGuid, cookie.UserAccessToken?.Edp, cookie.Language],
                        result.data as TmCart,
                    );
                    if (cookie.CartMerge == 'true') {
                        cartResponse.IsCartMerged = true;
                        cookie.deleteCartMerge();
                    }
                    setCartData(cartResponse);
                    cookie.UserFraudStatus = cartResponse.Buyer.IsFraud;
                }
                let redirectToCart = false;
                if (cartResponse) {
                    if (cartResponse.OrderSummary && cartResponse.OrderSummary.TotalQuantity < 1) {
                        redirectToCart = true;
                    }
                    if (cartResponse.Buyer && cartResponse.Buyer.IsFraud) {
                        redirectToCart = true;
                    }
                    if (
                        cartResponse.CartMessages &&
                        (cartResponse.CartMessages.OutOfStockMessage ||
                            cartResponse.CartMessages.MaxCheckoutLimitReached ||
                            cartResponse.CartMessages.MaxCartLineCountReached ||
                            cartResponse.CartMessages.MaxQuantityPerCartLineReached)
                    ) {
                        redirectToCart = true;
                    }

                    if (redirectToCart) {
                        localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');
                        window.location.href = '/pages/shoppingbag';
                    }
                }
                analyticsData.cart = cartResponse;
            } catch (error) {
                console.log('error:' + error);
                window.location.href = '/pages/shoppingbag';
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        //trigger analytics call when checkoutstep changes, but exclude review order step
        if (cartData != null) {
            //scroll to top of page on checkout step change
            window.scrollTo(0, 0);
            const trackStepChanged = (attempts = 0) => {
                if (typeof window['_satellite'] !== 'undefined') {
                    window['_satellite'].track('step_changed');
                } else if (attempts < 10) {
                    setTimeout(() => trackStepChanged(attempts + 1), 300);
                }
            };
            trackStepChanged();
        }
    }, [checkoutStep, cartData]);

    const persister = createSyncStoragePersister({
        storage: window.localStorage,
    });
    return (
        <>
            {cartData ? (
                <CartContextProvider value={{ cartData }}>
                    <div className={pageCssClass()}>
                        <ShippingFeeNoticeModal checkoutStep={checkoutStep} />
                        <Sticky
                            stickyStyle={{ zIndex: '1', background: '#fff' }}
                            stickyClassName={'sticky__header'}
                            positionRecheckInterval={11}
                        >
                            <Header progress={checkoutStep} />
                        </Sticky>
                        <div className="checkoutWrap">
                            <article className="leftSide">
                                <div className="stickySpacer"></div>
                                <NotificationMsg isQuebec={isQuebec} setIsQuebec={setIsQuebec} />
                                <ItemSummary />

                                {(() => {
                                    switch (checkoutStep) {
                                        case 'create-account':
                                            return <CreateAccount setCheckoutStep={setCheckoutStep} />;
                                        case 'payment':
                                            return <CreatePayment setCheckoutStep={setCheckoutStep} />;
                                        default:
                                            return (
                                                <>
                                                    <ShipByNote />
                                                    <ShippingAddress />
                                                    <ShippingMethod />
                                                    <PaymentMethod setCheckoutStep={setCheckoutStep} />
                                                    <PaymentOption />
                                                </>
                                            );
                                    }
                                })()}
                            </article>
                            <aside className="rightSide">
                                <div className="tablet-up-only">
                                    <FreeShippingNotice />
                                </div>
                                <RenderPropSticky
                                    hideOnBoundaryHit={false}
                                    boundaryElement=".rightSide"
                                    scrollElement="window"
                                    positionRecheckInterval={300}
                                >
                                    {({ isFixed, wrapperStyles, wrapperRef, holderStyles, holderRef }) => (
                                        <div ref={holderRef} style={holderStyles}>
                                            <div
                                                className={`sticky__order-summary ${
                                                    isFixed ? 'sticky__order-summary--stuck' : ''
                                                }`}
                                                style={isFixed ? { ...wrapperStyles } : wrapperStyles}
                                                ref={wrapperRef}
                                            >
                                                <div className="stickySpacer"></div>
                                                <div className="ordersummary__container">
                                                    <div className="mobile-only">
                                                        <FreeShippingNotice />
                                                    </div>

                                                    {(() => {
                                                        switch (checkoutStep) {
                                                            case 'create-account':
                                                                return (
                                                                    <>
                                                                        <OrderSummary checkoutStep={checkoutStep} />
                                                                        <Promocode />
                                                                        <ContinueToPayment />
                                                                    </>
                                                                );
                                                            case 'payment':
                                                                return (
                                                                    <>
                                                                        <OrderSummary checkoutStep={checkoutStep} />
                                                                        <AccountCredit />
                                                                        <Promocode />
                                                                        <Giftcard />
                                                                        <ContinueToReview />
                                                                    </>
                                                                );
                                                            default:
                                                                return (
                                                                    <>
                                                                        <OrderSummary checkoutStep={checkoutStep} />
                                                                        <AccountCredit />
                                                                        <Promocode />
                                                                        <Giftcard />
                                                                        <Placeorder scrollToTop={scrollToTop} />
                                                                    </>
                                                                );
                                                        }
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </RenderPropSticky>
                            </aside>
                        </div>
                        <footer>
                            <Loading />
                            <a href="/go/aboutus/aboutustandc" target="_blank" className="footer__links w-[253px]">
                                {t('Terms of Use')}
                            </a>
                            <span className="footer__separater">|</span>
                            <a href="/go/aboutus/aboutusprivacy" target="_blank" className="footer__links">
                                {t('Privacy Policy')}
                            </a>
                            <span className="footer__separater">|</span>
                            <a
                                href="/pages/customer-service/delivery-methods"
                                target="_blank"
                                className="footer__links"
                            >
                                {t('Shipping Conditions')}
                            </a>
                            {isQuebec && <LanguageToggle isQuebec={isQuebec} setIsQuebec={setIsQuebec} />}
                        </footer>
                    </div>
                </CartContextProvider>
            ) : (
                <div className="compLoading">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 100 100"
                        width="50"
                        height="50"
                        preserveAspectRatio="xMidYMid"
                    >
                        <path fill="none" d="M0 0h100v100H0z" />
                        <circle cx="50" cy="50" r="40" stroke="#000" fill="none" strokeWidth="6" strokeLinecap="round">
                            <animate
                                attributeName="stroke-dashoffset"
                                dur="3s"
                                repeatCount="indefinite"
                                from="502"
                                to="0"
                            />
                            <animate
                                attributeName="stroke-dasharray"
                                dur="3s"
                                repeatCount="indefinite"
                                values="238.45 12.550000000000011;1 250;238.45 12.550000000000011"
                            />
                        </circle>
                    </svg>
                </div>
            )}
        </>
    );
};

export default ExpressCheckout;
